import clsx from "clsx";
import dayjs, { Dayjs } from "dayjs";
import React, { FC, memo } from "react";
import { DatePicker } from "@pd/pd-ui-kit";
import type { Control, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form/dist/index.ie11";

import s from "./styles.less";

interface PdDatePickerFieldProps extends Omit<
  IPdDatePickerRawProps,
  "value" | "onChange"
> {
  name: string;
  control: Control<FieldValues>;
  defaultValue?: Date | null;
}

const PdDatePickerField: FC<PdDatePickerFieldProps> = ({
  name,
  control,
  defaultValue = null,
  ...datePickerProps
}) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ value, onChange }) => (
        <PdDatePickerRaw
          value={value}
          onChange={onChange}
          {...datePickerProps}
        />
      )}
    />
  );
};

interface IPdDatePickerRawProps {
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  dateFormat?: string;
  showTimeSelect?: boolean;
  showMonthYear?: boolean;
  showYearPicker?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  filterDate?: (date: Date) => boolean;
  clearDisabled?: boolean;
  customClassName?: string;
  value: Date | null | undefined;
  onChange: (value: Date | null | undefined) => void;
}

const mapLegacyFormat = (format: string) =>
  format.replace(/yyyy/g, "YYYY").replace(/dd/g, "DD").replace(/hh/g, "HH");

export const PdDatePickerRaw: FC<IPdDatePickerRawProps> = ({
  error,
  disabled = false,
  placeholder,
  dateFormat,
  showTimeSelect,
  showMonthYear = false,
  showYearPicker = false,
  minDate,
  maxDate,
  filterDate,
  clearDisabled,
  customClassName,
  value,
  onChange,
}) => {
  const picker = showYearPicker ? "year" : showMonthYear ? "month" : "date";

  const format = dateFormat
    ? mapLegacyFormat(dateFormat)
    : picker === "year"
      ? "YYYY"
      : picker === "month"
        ? "MM.YYYY"
        : showTimeSelect
          ? "DD.MM.YYYY, HH:mm"
          : "DD.MM.YYYY";

  const handleChange = (date: Dayjs | null) => {
    if (!date) return onChange(undefined);
    const normalized = showMonthYear
      ? date.endOf("month")
      : picker !== "date"
        ? date.startOf(picker)
        : date;
    onChange(normalized.toDate());
  };

  const disabledDate =
    minDate || maxDate || filterDate
      ? (current: Dayjs) => {
          if (!current) return false;
          if (minDate && current.isBefore(dayjs(minDate), "day")) return true;
          if (maxDate && current.isAfter(dayjs(maxDate), "day")) return true;
          if (filterDate && !filterDate(current.toDate())) return true;
          return false;
        }
      : undefined;

  return (
    <div className={clsx(s.container, customClassName)}>
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={handleChange}
        picker={picker}
        format={format}
        showTime={showTimeSelect}
        disabled={disabled}
        placeholder={placeholder}
        allowClear={!showYearPicker && !disabled && !clearDisabled}
        disabledDate={disabledDate}
        status={error ? "error" : undefined}
        style={{ width: "100%" }}
      />
      {error && <div className={s.error}>{error}</div>}
    </div>
  );
};

export default memo(PdDatePickerField);
